import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRightLeft,
  Building2,
  CornerDownRight,
  ExternalLink,
  Inbox,
  Lock,
  MailOpen,
  MousePointerClick,
  Pause,
  Play,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  User,
} from "lucide-react";
import {
  errorMessage,
  getCampaignContact,
  type CampaignContact,
  type CampaignContactDetail,
  type CampaignEvent,
  type ContactAction,
  type ConversationItem,
  type EmailMeta,
  type EmailReply,
  type StepTimeline,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import {
  BusinessStatusBadge,
  ContactStatusBadge,
  Hint,
  ScopeBadge,
  TonePill,
} from "@/components/email/EmailBadges";
import { AnnotatedText } from "@/components/email/AnnotatedText";
import { StepProgress } from "@/components/email/StepProgress";
import { EVENT_LABEL, REPLY_CONTENT, messageTone, shortAgo, trackingParts, workflowHold } from "@/lib/email";
import { campaignPath } from "@/lib/emailRoutes";
import { formatDate, formatDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";

type Mode = "workflow" | "conversation" | "activity";

/** One person in one campaign, read on demand: each step's exact email and
 *  what came of it (Workflow), the same emails and the replies in time order
 *  (Conversation), and every event Lemlist reported (Activity). */
export function CampaignContactSheet({
  membershipId,
  meta,
  slug,
  onOpenChange,
  onAction,
}: {
  membershipId: number | null;
  meta: EmailMeta;
  slug: string;
  onOpenChange: (open: boolean) => void;
  onAction?: (action: ContactAction | "move", row: CampaignContact) => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<CampaignContactDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("workflow");

  useEffect(() => {
    if (membershipId === null) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    setMode("workflow");
    getCampaignContact(slug, membershipId)
      .then((d) => !cancelled && setDetail(d))
      .catch((err) => !cancelled && setError(errorMessage(err, "This contact could not be loaded.")));
    return () => {
      cancelled = true;
    };
  }, [slug, membershipId]);

  const access = meta.project;
  const project = access.name;
  const run = async (action: ContactAction | "move") => {
    if (!detail || !onAction) return;
    await onAction(action, detail);
    if (action !== "move") onOpenChange(false);
  };
  const replies = detail?.conversation.filter((c) => c.direction === "INBOUND").length ?? 0;

  return (
    <Sheet open={membershipId !== null} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(1120px,92vw)]">
        <SheetHeader className="space-y-0 border-b border-slate-100 pb-0 pl-6 pr-14 pt-6 text-left">
          <SheetDescription className="sr-only">
            This contact's email workflow, conversation and activity in the selected campaign.
          </SheetDescription>
          {detail ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <ScopeBadge scope={detail.scope} />
                    {project && <span className="truncate">{project}</span>}
                    <span className="text-slate-300">/</span>
                    <Link
                      to={campaignPath(detail.project_slug, detail.campaign_id, "leads", { lead: detail.id })}
                      className="truncate font-medium text-slate-600 hover:text-indigo-600"
                      title="Open this campaign's leads"
                    >
                      {detail.campaign_name}
                    </Link>
                  </div>
                  <SheetTitle className="mt-2 text-2xl tracking-tight">{detail.contact_name}</SheetTitle>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {[detail.contact_title, detail.company_name].filter(Boolean).join(" · ") || "—"}
                    <span className="mx-1.5 text-slate-300">·</span>
                    {isHidden(detail, "email") ? (
                      <HiddenDetail label="Email" className="text-xs" />
                    ) : (
                      <span>{detail.email ?? "No email"}</span>
                    )}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {access.can_pause && detail.status === "EMAIL_PAUSED" && onAction && (
                    <Button size="sm" variant="outline" onClick={() => void run("resume")}>
                      <Play className="h-3.5 w-3.5" /> Resume
                    </Button>
                  )}
                  {access.can_pause &&
                    onAction &&
                    ["EMAIL_READY", "EMAIL_QUEUED", "EMAIL_ACTIVE"].includes(detail.status) && (
                      <Button size="sm" variant="outline" onClick={() => void run("pause")}>
                        <Pause className="h-3.5 w-3.5" /> Pause
                      </Button>
                    )}
                  {access.can_enroll && onAction && detail.status !== "EMAIL_REMOVED" && (
                    <Button size="sm" variant="outline" onClick={() => void run("move")}>
                      <ArrowRightLeft className="h-3.5 w-3.5" /> Change campaign
                    </Button>
                  )}
                  {access.can_remove && onAction && detail.status !== "EMAIL_REMOVED" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() => void run("remove")}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl bg-slate-50/80 px-4 py-3">
                <Fact label="Email status">
                  <ContactStatusBadge status={detail.status} reason={detail.status_reason} />
                </Fact>
                <Fact label="Business status">
                  <BusinessStatusBadge
                    status={detail.business_status}
                    label={detail.business_status_label}
                    statuses={meta.business_statuses}
                  />
                </Fact>
                <Fact label="Current step">
                  <StepProgress current={detail.current_step} total={detail.steps_count} />
                </Fact>
                <Fact label="Last event">
                  <span className="text-sm text-slate-700">
                    {detail.last_event_type ? (
                      <>
                        {EVENT_LABEL[detail.last_event_type] ?? detail.last_event_type}
                        <span className="text-slate-400" title={formatDateTime(detail.last_event_at)}>
                          {" "}
                          · {shortAgo(detail.last_event_at)}
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-400">Nothing yet</span>
                    )}
                  </span>
                </Fact>
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    onClick={() => navigate(`/projects/${detail.project_slug}/contacts?contact=${detail.contact_id}`)}
                  >
                    <User className="h-3.5 w-3.5" /> Contact
                  </Button>
                  {detail.company_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      onClick={() => navigate(`/projects/${detail.project_slug}/companies/${detail.company_id}`)}
                    >
                      <Building2 className="h-3.5 w-3.5" /> Company
                    </Button>
                  )}
                  {detail.provider_url && (
                    <Button size="sm" variant="ghost" className="h-8" asChild>
                      <a href={detail.provider_url} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" /> Lemlist
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              {detail.status_reason && (
                <p className="rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2 text-xs text-amber-900">
                  {detail.status_reason}
                </p>
              )}
              <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
                <TabsList>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="conversation">
                    Conversation
                    {replies > 0 && (
                      <span className="ml-1.5 rounded-full bg-emerald-50 px-1.5 text-[11px] font-semibold text-emerald-700">
                        {replies}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="activity">
                    Activity
                    <span className="ml-1.5 text-[11px] tabular-nums text-slate-400">{detail.events.length}</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          ) : (
            <div className="space-y-2 pb-6">
              <SheetTitle className="sr-only">Contact in campaign</SheetTitle>
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-8 w-72" />
              <Skeleton className="h-4 w-52" />
              <Skeleton className="mt-4 h-14 w-full" />
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto bg-slate-50/40">
          {error && <p className="px-6 py-5 text-sm text-rose-600">{error}</p>}
          {!detail && !error && (
            <div className="flex gap-4 px-6 py-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-72 w-80 shrink-0 rounded-xl" />
              ))}
            </div>
          )}
          {detail && mode === "workflow" && (
            <Workflow
              steps={detail.steps}
              status={detail.status}
              dryRun={detail.dry_run}
              scheduleHold={detail.schedule_hold ?? null}
            />
          )}
          {detail && mode === "conversation" && (
            <Conversation items={detail.conversation} contactName={detail.contact_name} />
          )}
          {detail && mode === "activity" && <Activity events={detail.events} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="flex items-center">{children}</div>
    </div>
  );
}

// ── Workflow: one column per step ─────────────────────────────────────────

function Workflow({
  steps,
  status,
  dryRun,
  scheduleHold,
}: {
  steps: StepTimeline[];
  status: CampaignContact["status"];
  dryRun: boolean;
  scheduleHold: string | null;
}) {
  if (!steps.length) {
    return <p className="px-6 py-8 text-sm text-slate-500">This campaign has no step.</p>;
  }
  return (
    <div className="flex items-start gap-4 overflow-x-auto px-6 py-5" data-testid="email-workflow">
      {steps.map((step, index) => (
        <StepColumn
          key={step.position}
          step={step}
          status={status}
          dryRun={dryRun}
          scheduleHold={scheduleHold}
          last={index === steps.length - 1}
        />
      ))}
    </div>
  );
}

function StepColumn({
  step,
  status,
  dryRun,
  scheduleHold,
  last,
}: {
  step: StepTimeline;
  status: CampaignContact["status"];
  dryRun: boolean;
  scheduleHold: string | null;
  last: boolean;
}) {
  const hold = workflowHold(step, status, dryRun);
  const tone = hold ?? messageTone(step.status);
  const tracking = trackingParts(step);
  const sent = !!step.sent_at;
  return (
    <section
      className="relative w-[330px] shrink-0 space-y-2.5"
      data-testid="email-step"
      aria-label={`Step ${step.position}`}
    >
      <header className="flex items-center justify-between gap-2 px-1">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-800">
            <span className="mr-1.5 tabular-nums text-slate-400">{step.position}</span>
            {step.step_name ?? "Deleted step"}
          </p>
          <p className="text-[11px] text-slate-400">
            {step.delay_days === null
              ? "—"
              : step.position === 1
                ? "First email"
                : step.delay_days === 0
                  ? "Same day as the previous email"
                  : `${step.delay_days} day${step.delay_days === 1 ? "" : "s"} after the previous email`}
          </p>
        </div>
        <TonePill tone={tone} />
      </header>
      {!last && (
        <span aria-hidden className="absolute -right-3 top-3 h-px w-2 bg-slate-200" />
      )}

      <article className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-card-rest">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            {sent ? (
              <>
                <Lock className="h-3 w-3 text-indigo-500" />
                <span title="Frozen: this is exactly what was sent.">Sent {formatDateTime(step.sent_at)}</span>
              </>
            ) : hold ? (
              <span>{hold.hint}</span>
            ) : step.planned_on ? (
              <>
                <Send className={cn("h-3 w-3", step.planned_overdue ? "text-amber-500" : "text-sky-500")} />
                <Hint
                  content={
                    step.planned_overdue
                      ? "Estimated for a day already past and not reported sent yet: Lemlist's queue, a pause there, or events not synced yet."
                      : "Estimated day, on the campaign's sending days. Lemlist chooses the time within the sending window."
                  }
                >
                  <span className={cn(step.planned_overdue && "font-medium text-amber-700")}>
                    {step.planned_overdue ? "Overdue · estimated " : "Estimated · "}
                    {formatDate(step.planned_on, "EEE d MMM")}
                  </span>
                </Hint>
              </>
            ) : (
              <span>{scheduleHold ?? "Not scheduled yet"}</span>
            )}
          </span>
          {step.template_version && <span>v{step.template_version}</span>}
        </div>
        {step.subject !== null || step.body !== null ? (
          <div className="space-y-2 px-4 py-3">
            <p className="text-sm font-medium text-slate-900">
              {step.subject ? (
                step.subject
              ) : (
                <span className="font-normal italic text-slate-400">No subject — stays in the thread</span>
              )}
            </p>
            <p className="max-h-64 overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed text-slate-600">
              {step.body !== null && <AnnotatedText text={step.body} />}
            </p>
          </div>
        ) : (
          <p className="px-4 py-4 text-[13px] text-slate-400">
            Rendered from the template when Prism hands the contact to Lemlist.
          </p>
        )}
        {step.outdated && (
          <p className="flex items-center gap-1.5 border-t border-amber-100 bg-amber-50/60 px-4 py-2 text-[11px] text-amber-800">
            <RefreshCw className="h-3 w-3" /> Template changed — refreshed at the next sync.
          </p>
        )}
        {Object.keys(step.ai_values).length > 0 && (
          <div className="space-y-1.5 border-t border-slate-100 px-4 py-2.5">
            <p className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-violet-500">
              <Sparkles className="h-3 w-3" /> AI values
            </p>
            {Object.entries(step.ai_values).map(([key, value]) => (
              <div key={key} className="text-[12px] leading-snug">
                <code className="mr-1.5 rounded bg-violet-50 px-1 py-0.5 text-[11px] text-violet-700">ai.{key}</code>
                <span className="text-slate-600">{value}</span>
              </div>
            ))}
          </div>
        )}
        {(tracking.length > 0 || step.bounced_at) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
            {step.open_count > 0 && (
              <Hint
                content={
                  step.first_opened_at
                    ? `First ${formatDateTime(step.first_opened_at)} · last ${formatDateTime(step.last_opened_at)}`
                    : undefined
                }
              >
                <span className="inline-flex items-center gap-1 text-violet-700">
                  <MailOpen className="h-3 w-3" /> {tracking[0]}
                </span>
              </Hint>
            )}
            {step.click_count > 0 && (
              <span className="inline-flex items-center gap-1 text-fuchsia-700">
                <MousePointerClick className="h-3 w-3" /> {tracking[tracking.length - 1]}
              </span>
            )}
            {step.bounced_at && <span className="text-rose-700">Bounced {shortAgo(step.bounced_at)}</span>}
          </div>
        )}
      </article>

      {step.replies.map((reply) => (
        <ReplyCard key={reply.id} reply={reply} />
      ))}

      {step.events.length > 0 && (
        <ol className="space-y-1 px-1">
          {step.events.map((event) => (
            <li key={event.id} className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{EVENT_LABEL[event.type] ?? event.type}</span>
              <span className="tabular-nums text-slate-400" title={formatDateTime(event.occurred_at)}>
                {formatDateTime(event.occurred_at, "MMM d, HH:mm")}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ReplyCard({ reply }: { reply: EmailReply }) {
  const missing = reply.content_state === "PENDING" || reply.content_state === "UNAVAILABLE";
  return (
    <article className="ml-4 overflow-hidden rounded-xl border border-emerald-200/80 bg-emerald-50/40" data-testid="email-reply">
      <div className="flex items-center justify-between gap-2 px-4 pt-2.5 text-[11px] font-medium text-emerald-800">
        <span className="inline-flex items-center gap-1.5">
          <CornerDownRight className="h-3 w-3" /> Reply
        </span>
        <span className="tabular-nums text-emerald-700/70">{formatDateTime(reply.received_at)}</span>
      </div>
      <div className="px-4 pb-3 pt-1.5">
        {missing ? (
          <p className="text-[13px] italic text-slate-500">{REPLY_CONTENT[reply.content_state as "PENDING" | "UNAVAILABLE"]}</p>
        ) : (
          <p className="max-h-60 overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed text-slate-800">
            {reply.body_text}
          </p>
        )}
      </div>
    </article>
  );
}

// ── Conversation: the emails and the replies, in time order ───────────────

function Conversation({ items, contactName }: { items: ConversationItem[]; contactName: string }) {
  if (!items.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <Inbox className="h-6 w-6 text-slate-300" />
        <p className="text-sm text-slate-500">Nothing was sent yet.</p>
      </div>
    );
  }
  return (
    <ol className="mx-auto max-w-3xl space-y-4 px-6 py-6" data-testid="email-conversation">
      {items.map((item, index) => {
        const inbound = item.direction === "INBOUND";
        const tracking = trackingParts(item);
        return (
          <li key={index} className={cn("flex", inbound ? "justify-end" : "justify-start")}>
            <article
              className={cn(
                "w-[85%] overflow-hidden rounded-2xl border shadow-card-rest",
                inbound ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-white",
              )}
            >
              <header className="flex items-center justify-between gap-3 px-4 pt-3 text-[11px]">
                <span className={cn("font-semibold", inbound ? "text-emerald-800" : "text-indigo-700")}>
                  {inbound ? contactName : "Prism"}
                  <span className="ml-1.5 font-normal text-slate-400">
                    {inbound ? "replied" : `step ${item.step_position ?? "—"}`}
                  </span>
                </span>
                <span className="tabular-nums text-slate-400">{formatDateTime(item.at)}</span>
              </header>
              <div className="space-y-1.5 px-4 pb-3 pt-1.5">
                {item.subject && <p className="text-sm font-medium text-slate-900">{item.subject}</p>}
                {inbound && item.content_state !== "AVAILABLE" ? (
                  <p className="text-[13px] italic text-slate-500">
                    {REPLY_CONTENT[item.content_state as "PENDING" | "UNAVAILABLE"]}
                  </p>
                ) : (
                  <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{item.body}</p>
                )}
                {!inbound && tracking.length > 0 && (
                  <p className="pt-1 text-[11px] text-violet-700">{tracking.join(" · ")}</p>
                )}
              </div>
            </article>
          </li>
        );
      })}
    </ol>
  );
}

// ── Activity: every event Lemlist reported ────────────────────────────────

function Activity({ events }: { events: CampaignEvent[] }) {
  if (!events.length) {
    return <p className="px-6 py-8 text-sm text-slate-500">Nothing reported by Lemlist yet.</p>;
  }
  return (
    <ol className="mx-auto max-w-3xl space-y-1.5 px-6 py-5">
      {events.map((event) => (
        <li
          key={event.id}
          className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm"
        >
          <span className="font-medium text-slate-700">
            {EVENT_LABEL[event.type] ?? event.type}
            {event.step_position && <span className="font-normal text-slate-400"> · step {event.step_position}</span>}
            <span className="ml-2 font-mono text-[11px] font-normal text-slate-400">{event.provider_type}</span>
          </span>
          <span className="text-xs tabular-nums text-slate-400">{formatDateTime(event.occurred_at)}</span>
        </li>
      ))}
    </ol>
  );
}
